<template>
  <div class="competition-query">
    <div class="query-tabs">
      <button
        v-for="tab in tabs"
        :key="tab.key"
        type="button"
        class="query-tab"
        :class="{ active: activeTab === tab.key }"
        @click="activeTab = tab.key"
      >
        {{ tab.label }}
      </button>
    </div>

    <div v-if="filteredActivities.length" class="query-grid">
      <article
        v-for="item in filteredActivities"
        :key="item.id"
        class="competition-card"
        :class="`status-${item.statusKey}`"
      >
        <div class="card-head">
          <div class="card-title">{{ item.title }}</div>
          <div class="status-badge">{{ item.statusText }}</div>
        </div>

        <div v-if="item.coverImage" class="card-image-wrap">
          <img :src="item.coverImage" :alt="item.title" class="card-image">
        </div>

        <div class="card-body"></div>

        <div class="card-footer">
          <div class="card-tip-slot">
            <div v-if="item.showEditTip" class="card-tip">
              您可前往参赛作品中，修改作品资料
            </div>
          </div>
          <button
            v-if="item.action.type === 'button'"
            type="button"
            class="action-button"
            @click="openCompetition(item)"
          >
            {{ item.action.text }}
          </button>
          <button
            v-else-if="item.action.type === 'disabled'"
            type="button"
            class="action-button action-button-disabled"
            disabled
          >
            {{ item.action.text }}
          </button>
          <div v-else class="action-text" :class="`action-${item.action.type}`">
            {{ item.action.text }}
          </div>
        </div>
      </article>
    </div>

    <div v-else class="empty-state">当前筛选下暂无大赛。</div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { activityDetail } from "@/api/Vote";
import { userVoteWorks } from "@/api/UserCenter";
import { resolveContestTimeline } from "@/utils/contestTimeline";
import { normalizeVoteOptionGroups } from "@/utils/voteMeta";
import { CONTEST_ACTIVITY_IDS } from "@/utils/contestActivity";
import { toDisplayImageUrl } from "@/utils/workImage";
const ACTIVITY_ORDER = [6, 5];

export default {
  name: "CompetitionQuery",
  data() {
    return {
      activeTab: "all",
      activities: [],
      signedActivityIds: [],
      signedWorkMap: {},
      tabs: [
        { key: "all", label: "全部" },
        { key: "signup", label: "报名中" },
        { key: "review", label: "投票中" },
        { key: "finished", label: "已结束/已完成" },
      ],
    };
  },
  computed: {
    ...mapGetters(["isLogin"]),
    filteredActivities() {
      if (this.activeTab === "all") {
        return this.activities;
      }
      return this.activities.filter((item) => item.statusKey === this.activeTab);
    },
  },
  mounted() {
    this.loadActivities();
  },
  methods: {
    async loadActivities() {
      try {
        const [details, signedActivityIds] = await Promise.all([
          this.loadActivityDetails(),
          this.loadSignedActivityIds(),
        ]);
        this.signedActivityIds = signedActivityIds;
        this.activities = details
          .filter(Boolean)
          .map((item) => this.normalizeActivity(item))
          .filter(Boolean)
          .sort((a, b) => this.getActivitySortWeight(a.id) - this.getActivitySortWeight(b.id));
      } catch (error) {
        this.$message.error(error?.data?.msg || error?.msg || "大赛信息加载失败");
      }
    },
    getActivitySortWeight(id) {
      const index = ACTIVITY_ORDER.indexOf(Number(id));
      return index === -1 ? ACTIVITY_ORDER.length + Number(id || 0) : index;
    },
    loadActivityDetails() {
      return Promise.all(
        CONTEST_ACTIVITY_IDS.map((id) =>
          activityDetail(id, { isShow: 0, uid: 0 }).then((res) => res.data).catch(() => null)
        )
      );
    },
    loadSignedActivityIds() {
      if (!this.isLogin) {
        this.signedWorkMap = {};
        return Promise.resolve([]);
      }
      return userVoteWorks({ page: 1, limit: 100 })
        .then((res) => {
          const raw = res && res.data;
          const records = Array.isArray(raw) ? raw : ((raw && Array.isArray(raw.list)) ? raw.list : []);
          this.signedWorkMap = records.reduce((acc, item) => {
            if (Number(item.status) === 1 || !item.voteWorksVo || !item.voteWorksVo.id) {
              return acc;
            }
            const activityId = Number(item.activityId || item.voteWorksVo.activityId || 0);
            if (activityId && !acc[activityId]) {
              acc[activityId] = Number(item.voteWorksVo.id);
            }
            return acc;
          }, {});
          return Object.keys(this.signedWorkMap).map((key) => Number(key)).filter(Boolean);
        })
        .catch(() => {
          this.signedWorkMap = {};
          return [];
        });
    },
    getSignedWorkId(activityId) {
      return Number(this.signedWorkMap[Number(activityId)] || 0);
    },
    normalizeActivity(item) {
      const normalizedOptions = normalizeVoteOptionGroups(item.plateVos, item.categoryVos);
      const isSigned = this.signedActivityIds.includes(Number(item.id));
      const statusInfo = this.resolveContestStatus(item);
      if (statusInfo.key === "hidden") {
        return null;
      }
      return {
        id: item.id,
        title: item.title,
        statusKey: statusInfo.key,
        statusText: statusInfo.text,
        isSigned,
        showEditTip: isSigned && statusInfo.key === "signup",
        action: this.resolveAction(statusInfo.key, isSigned),
        coverImage: toDisplayImageUrl(item.image),
        startDate: this.formatDate(item.startTime),
        endDate: this.formatDate(item.endTime),
        overDate: this.formatDate(item.overTime),
        plateSummary: normalizedOptions.plateVos.slice(0, 3).map((entry) => entry.name).join(" / "),
        categorySummary: normalizedOptions.categoryVos.slice(0, 3).map((entry) => entry.name).join(" / "),
      };
    },
    resolveContestStatus(item) {
      const status = resolveContestTimeline(item);
      if (status.key === "voting") {
        return { key: "review", text: "投票中" };
      }
      if (status.key === "ended") {
        return { key: "finished", text: "已结束" };
      }
      if (status.key === "completed") {
        return { key: "finished", text: "已完成" };
      }
      return status;
    },
    resolveAction(statusKey, isSigned) {
      if (!isSigned) {
        if (statusKey === "review") {
          return { type: "button", text: "查看投票", target: "vote" };
        }
        return { type: "button", text: "查看赛事", target: "vote" };
      }
      if (statusKey === "signup") {
        return { type: "button", text: "查看作品", target: "works" };
      }
      if (statusKey === "review") {
        return { type: "button", text: "查看投票", target: "vote" };
      }
      return { type: "muted", text: "已完成" };
    },
    formatDate(value) {
      if (!value) {
        return "--";
      }
      return String(value).slice(0, 10);
    },
    openCompetition(item) {
      if (item.action && item.action.target === "works") {
        this.$router.push({
          name: "UserCenter",
          query: {
            id: 6,
            activityId: String(item.id),
          },
        });
        return;
      }
      this.$router.push({ name: "WorkCompetition", params: { id: String(item.id) } });
    },
  },
};
</script>

<style lang="scss" scoped>
.competition-query {
  .query-tabs {
    display: flex;
    gap: 28px;
    margin-bottom: 28px;
    padding-bottom: 14px;
    border-bottom: 1px solid #ece7e1;
  }

  .query-tab {
    position: relative;
    border: none;
    background: transparent;
    padding: 0;
    color: #8f8780;
    font-size: 16px;
    cursor: pointer;
  }

  .query-tab.active {
    color: #3f342d;
    font-weight: 600;
  }

  .query-tab.active::after {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    bottom: -15px;
    height: 3px;
    border-radius: 999px;
    background: #e48642;
  }

  .query-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 20px;
  }

  .competition-card {
    display: flex;
    flex-direction: column;
    border: 1px solid #e7ded5;
    border-radius: 14px;
    overflow: hidden;
    background: #fff;
    box-shadow: 0 10px 24px rgba(82, 58, 43, 0.06);
  }

  .competition-card.status-signup {
    border-color: #efc39e;
  }

  .competition-card.status-review {
    border-color: #b7d7d3;
  }

  .competition-card.status-finished {
    border-color: #dddddd;
    opacity: 0.86;
  }

  .card-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    padding: 18px 18px 14px;
    min-height: 84px;
  }

  .status-signup .card-head {
    background: linear-gradient(135deg, #f0995b, #e9b078);
  }

  .status-review .card-head {
    background: linear-gradient(135deg, #8fc5c0, #b5d8d4);
  }

  .status-finished .card-head {
    background: linear-gradient(135deg, #d7d7d7, #ececec);
  }

  .card-title {
    color: #fff;
    line-height: 1.5;
    font-size: 16px;
    font-weight: 600;
  }

  .status-finished .card-title {
    color: #5f5f5f;
  }

  .status-badge {
    flex: 0 0 auto;
    padding: 6px 10px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.22);
    color: #fff;
    font-size: 12px;
  }

  .status-finished .status-badge {
    background: rgba(95, 95, 95, 0.12);
    color: #666;
  }

  .card-image-wrap {
    width: calc(100% - 28px);
    margin: 14px;
  }

  .card-image {
    display: block;
    width: 100%;
    height: auto;
    border-radius: 10px;
  }

  .card-body {
    flex: 1 1 auto;
    padding: 0 18px 16px;
  }

  .meta-line {
    display: flex;
    gap: 12px;
    margin-bottom: 10px;
    color: #5f5750;
    font-size: 13px;
    line-height: 1.6;
  }

  .meta-label {
    flex: 0 0 68px;
    color: #c06f2d;
  }

  .card-footer {
    margin-top: auto;
    padding: 0 18px 18px;
  }

  .card-tip-slot {
    min-height: 52px;
    margin-bottom: 12px;
  }

  .card-tip {
    padding: 10px 12px;
    border-radius: 10px;
    background: linear-gradient(180deg, rgba(228, 134, 66, 0.08), rgba(228, 134, 66, 0.04));
    color: #9b5c29;
    font-size: 12px;
    line-height: 1.6;
    text-align: center;
  }

  .action-button,
  .action-text {
    width: 100%;
    min-height: 42px;
    border-radius: 10px;
    font-size: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .action-button {
    border: none;
    background: #e48642;
    color: #fff;
    cursor: pointer;
  }

  .action-button-disabled {
    background: rgba(176, 167, 159, 0.2);
    color: #b0a79f;
    cursor: not-allowed;
  }

  .action-text {
    padding: 0 14px;
    text-align: center;
  }

  .action-muted {
    color: #b0a79f;
    background: rgba(176, 167, 159, 0.12);
  }

  .empty-state {
    padding: 80px 0;
    text-align: center;
    color: #9d948b;
    font-size: 15px;
  }

  @media (max-width: 1200px) {
    .query-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  @media (max-width: 768px) {
    .query-tabs {
      gap: 18px;
      overflow-x: auto;
    }

    .query-grid {
      grid-template-columns: 1fr;
    }
  }
}
</style>
